import React, { useCallback, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Box } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { useAppConfig } from '@/hooks/useAppConfig';
import { CourseFragment } from '@/modules/corporate/graphql/fragments/generated/Course';
import { useCreateLocalizedContentsMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/createLocalizedContents';
import { LanguagesProps } from '@/modules/corporate/modules/course/views/CourseBuilder/components/SidebarDetails/Settings/types';
import { LanguageEnum } from '@/types/schema';

import AddLanguages from './addLanguages';
import ViewContent from './viewContent';

const Languages: React.FC<LanguagesProps> = ({ course, onClose, reexecuteQuery, item }) => {
  const { t } = useTranslation();
  const [open, { toggle }] = useToggle();
  const {
    languageConfigs = [],
    languageType: languageType_,
    id,
    type,
    defaultLanguageType,
  } = (course ?? item) as CourseFragment;

  const languageType = !!languageType_
    ? languageType_
    : defaultLanguageType ?? LanguageEnum.English;
  const [allLanguageConfigs, setallLanguageConfigs] = useState(languageConfigs);
  const {
    site: { supportedLanguages },
  } = useAppConfig();
  const [{ fetching: isCreatingLocalizedContent }, createLocalizedContent] =
    useCreateLocalizedContentsMutation();

  const allLanguagesMap = useMemo(() => {
    const allLanguagesMap: { [key: string]: string } = {};
    Object.entries(LanguageEnum).forEach(([key, value]) => (allLanguagesMap[value] = key));
    return allLanguagesMap;
  }, []);

  const onAddLanguages = useCallback(
    async (languages: LanguageEnum[]) => {
      const createLocalizedContentInput = {
        entityId: id,
        languageTypes: languages,
      };
      const result = await createLocalizedContent({ input: createLocalizedContentInput });
      const addedLanguageConfigs = result.data?.createLocalizedContents.languageConfigs;
      if (addedLanguageConfigs?.length) {
        allLanguageConfigs?.push(...addedLanguageConfigs);
      }
      reexecuteQuery?.({ requestPolicy: 'network-only' });
      setallLanguageConfigs(allLanguageConfigs);
    },
    [createLocalizedContent, allLanguageConfigs, reexecuteQuery],
  );

  const initialSelectedOptions = useMemo(() => {
    if (languageType && languageConfigs) {
      const mainLang = languageConfigs.find(
        (languageConfig) => languageConfig.languageType == languageType,
      );
      if (!mainLang) {
        setallLanguageConfigs([...languageConfigs, { contentId: id, languageType }]);
      }
    }
    const initialSelectedOptions = allLanguageConfigs?.map((languageConfig) => {
      return {
        value: languageConfig.languageType,
        label: allLanguagesMap[languageConfig.languageType],
      };
    });
    return initialSelectedOptions;
  }, [languageType]);

  const disableAddButton = useMemo(() => {
    const disableButton =
      isCreatingLocalizedContent ||
      (allLanguageConfigs?.length ?? 0) == (supportedLanguages?.length ?? 0);
    return disableButton;
  }, [supportedLanguages, allLanguageConfigs, isCreatingLocalizedContent]);

  return (
    <Modal
      header={{
        title: t('Course:Form.Languages'),
        description: t('Course:Form.LanguageDescription'),
      }}
      variant={ModalVariant.FullHeight}
      onClose={onClose}>
      <Box className="flex flex-1 flex-col px-24 pb-24">
        {allLanguageConfigs?.map((config) => (
          <ViewContent
            key={config.contentId}
            allLanguagesMap={allLanguagesMap}
            languageConfig={config}
            languageType={languageType}
            resourceType={type}
            t={t}
          />
        ))}

        <AddLanguages
          disableAddButton={disableAddButton}
          initialSelectedOptions={initialSelectedOptions ?? []}
          isCreatingLocalizedContent={isCreatingLocalizedContent}
          open={open}
          onChange={onAddLanguages}
          onToggle={toggle}
        />
      </Box>
    </Modal>
  );
};

export default React.memo(Languages);
